import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { pb, Cooperative, Gallery, Member } from '@/lib/pocketbase';
import { ArrowLeft } from "lucide-react";
import { Footer } from "@/components/product/Footer";

export const Sahakari: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [cooperative, setCooperative] = useState<Cooperative | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [gallery, setGallery] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    let isSubscribed = true;

    const loadData = async () => {
      try {
        // Get cooperative with proper typing
        const cooperativeRecord = await pb
          .collection('cooperatives')
          .getFirstListItem<Cooperative>(`slug="${slug}"`, {
            $cancelKey: 'cooperative-fetch'
          });

        // Then fetch related members and gallery with proper typing
        const [membersData, galleryData] = await Promise.all([
          pb.collection('members').getList<Member>(1, 50, {
            filter: `cooperative="${cooperativeRecord.id}"`,
            sort: 'order',
            $cancelKey: 'members-fetch'
          }),
          pb.collection('gallery').getList<Gallery>(1, 50, {
            filter: `cooperative="${cooperativeRecord.id}"`,
            $cancelKey: 'gallery-fetch'
          })
        ]);
          
        if (isSubscribed) {
          setCooperative(cooperativeRecord);
          setMembers(membersData.items);
          setGallery(galleryData.items);
        }
      } catch (error: any) {
        // Only navigate away if it's not an auto-cancellation error
        if (error.name !== 'ClientResponseError' || error.status !== 0) {
          console.error('Error loading data:', error);
          if (isSubscribed) {
            navigate('/');
          }
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    if (slug) {
      loadData();
    }

    return () => {
      isSubscribed = false;
      // Cancel any pending requests
      pb.cancelRequest('cooperative-fetch');
      pb.cancelRequest('members-fetch');
      pb.cancelRequest('gallery-fetch');
    };
  }, [slug, navigate]);

  useEffect(() => {
    if (gallery.length > 0) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
      }, 5000); // Change image every 3 seconds

      return () => clearInterval(timer);
    }
  }, [gallery.length]);

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-[rgba(0,63,40,1)]">Loading...</div>
      </div>
    );
  }

  if (!cooperative) return null;

  return (
    <div className="bg-white flex w-full flex-col items-center mx-auto">
      <div className="w-full max-w-[500px] overflow-hidden">
        {/* Hero Section */}
        <div className="relative w-full">
          <img
            src={pb.files.getURL(cooperative!, cooperative!.image)}
            alt={cooperative!.name}
            className="w-full h-[50vh] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(0,63,40,0.8)]" />
          <button 
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Cooperative Info */}
        <div className="px-4 -mt-20 relative z-10">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h1 className="text-3xl font-bold text-[rgba(0,63,40,1)] mb-2">
              {cooperative!.name}
            </h1>
            <div className="flex items-center gap-2 text-sm text-[rgba(0,63,40,0.7)] mb-4">
              <span>📍</span>
              <span>{cooperative!.location}</span>
            </div>
            <div className="prose prose-green max-w-none text-[rgba(0,63,40,0.9)]">
              <div dangerouslySetInnerHTML={{ __html: cooperative!.description }} />
            </div>
          </div>
        </div>

        {/* Members Section */}
        {members.length > 0 && (
          <div className="px-4 mt-12">
            <h2 className="text-2xl font-bold text-[rgba(0,63,40,1)] mb-8">
              Our Members
            </h2>
            <div className="grid grid-cols-1 gap-6">
              {members
                .sort((a, b) => a.order - b.order)
                .map((member) => (
                  <div 
                    key={member.id}
                    className="bg-[rgba(214,255,235,0.5)] rounded-2xl p-6 flex flex-col items-center"
                  >
                    <div className="w-48 h-48 rounded-full overflow-hidden mb-4 shadow-lg">
                      <img
                        src={pb.files.getURL(member, member.image)}
                        alt={member.name}
                        className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-[rgba(0,63,40,1)] font-bold text-2xl mb-2">
                        {member.name}
                      </h3>
                      <div className="bg-[rgba(0,63,40,1)] text-white px-4 py-1 rounded-full text-sm inline-block">
                        {member.role}
                      </div>
                      {member.description && (
                        <p className="text-[rgba(0,63,40,0.8)] mt-4 leading-relaxed">
                          {member.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Gallery Section */}
        {gallery.length > 0 && (
          <div className="px-4 mt-12 mb-8">
            <h2 className="text-2xl font-bold text-[rgba(0,63,40,1)] mb-6">
              Gallery
            </h2>
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-[rgba(214,255,235,0.5)]">
              {/* Main Image */}
              <div className="absolute inset-0 transition-opacity duration-1000">
                <img
                  src={pb.files.getURL(gallery[currentImageIndex], gallery[currentImageIndex].image)}
                  alt="Gallery image"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Navigation Dots */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                {gallery.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'bg-[rgba(0,63,40,1)] w-4' 
                        : 'bg-white/70'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={() => setCurrentImageIndex((prev) => 
                  prev === 0 ? gallery.length - 1 : prev - 1
                )}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                aria-label="Previous image"
              >
                ←
              </button>
              <button
                onClick={() => setCurrentImageIndex((prev) => 
                  (prev + 1) % gallery.length
                )}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                aria-label="Next image"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};